import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, ExternalLink, Sparkles, GripVertical } from "lucide-react";
import ImageUploadField from "../../components/admin/ImageUploadField";
import ArticleAiCopilot from "../../components/admin/learn/ArticleAiCopilot";
import BulkFaqModal from "../../components/admin/learn/BulkFaqModal";

const EMPTY_T = {
  name: "",
  age: "",
  location: "",
  condition: "",
  problem: "",
  solution: "",
  result: "",
  story_short: "",
  quote: "",
  timeline: "",
  before_image_url: "",
  after_image_url: "",
  video_url: "",
  video_poster_url: "",
  linked_products: [],
  cta_text: "",
  language: "en",
  is_active: true,
  sort_order: 0,
};

export function TransformationsTab() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_T);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [trans, prods] = await Promise.all([
        api.get("/admin/transformations"),
        api.get("/products"),
      ]);
      setItems(trans.data || []);
      setProducts(prods.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing({ ...EMPTY_T, linked_products: [] }); setOpen(true); };
  const startEdit = (t) => {
    setEditing({
      ...t,
      linked_products: (t.linked_products || []).map((lp) => ({ ...lp })),
      cta_text: t.cta_text || "",
      language: t.language || "en",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const linked = (editing.linked_products || [])
        .filter((lp) => lp.product_id)
        .map((lp, idx) => ({
          product_id: lp.product_id,
          reason: (lp.reason || "").trim(),
          sort_order: Number(lp.sort_order ?? idx) || idx,
        }));
      const payload = {
        ...editing,
        age: editing.age === "" ? null : Number(editing.age),
        linked_products: linked,
        cta_text: (editing.cta_text || "").trim() || null,
        language: ["en", "hi", "all"].includes(editing.language) ? editing.language : "en",
      };
      if (editing.id) {
        await api.put(`/admin/transformations/${editing.id}`, payload);
      } else {
        await api.post(`/admin/transformations`, payload);
      }
      toast.success("Saved");
      setOpen(false);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this transformation?")) return;
    try {
      await api.delete(`/admin/transformations/${id}`);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-transformations-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display text-lg text-emerald-950">Transformation stories</h3>
            <p className="text-xs text-emerald-900/60">Problem → Solution → Result format. Shown on the landing page.</p>
          </div>
          <Button onClick={startNew} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-trans-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New story
          </Button>
        </div>

        <div className="space-y-3">
          {items.length === 0 ? (
            <p className="text-sm text-emerald-900/60">No transformations yet — add your first.</p>
          ) : items.map((t) => (
            <div key={t.id} className="rounded-xl border border-emerald-100 p-4 flex items-start gap-3" data-testid={`admin-trans-row-${t.id}`}>
              <div className="grow min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-emerald-950">{t.name}</span>
                  {t.condition ? <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{t.condition}</span> : null}
                  {!t.is_active ? <span className="text-[11px] uppercase tracking-[0.18em] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Hidden</span> : null}
                </div>
                <div className="text-xs text-emerald-900/60 mt-1">{t.result}</div>
                <div className="text-xs text-emerald-900/60 mt-1 line-clamp-2">{t.problem}</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => startEdit(t)} className="rounded-full text-emerald-700" data-testid={`admin-trans-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(t.id)} className="rounded-full text-red-600" data-testid={`admin-trans-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-trans-dialog">
          <DialogHeader>
            <DialogTitle>{editing.id ? "Edit transformation" : "New transformation"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="admin-trans-name" />
            <Field label="Age" value={editing.age} onChange={(v) => setEditing({ ...editing, age: v })} testid="admin-trans-age" type="number" />
            <Field label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="admin-trans-location" />
            <Field label="Condition" value={editing.condition} onChange={(v) => setEditing({ ...editing, condition: v })} testid="admin-trans-condition" />
          </div>
          <Field area label="Problem" value={editing.problem} onChange={(v) => setEditing({ ...editing, problem: v })} testid="admin-trans-problem" rows={3} />
          <Field area label="Solution" value={editing.solution} onChange={(v) => setEditing({ ...editing, solution: v })} testid="admin-trans-solution" rows={3} />
          <Field label="Result" value={editing.result} onChange={(v) => setEditing({ ...editing, result: v })} testid="admin-trans-result" />
          <Field area label="Short story (italic blockquote)" value={editing.story_short} onChange={(v) => setEditing({ ...editing, story_short: v })} testid="admin-trans-shortstory" rows={2} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Quote (overlay on reel card)" value={editing.quote} onChange={(v) => setEditing({ ...editing, quote: v })} testid="admin-trans-quote" />
            <Field label='Timeline (e.g. "12 weeks")' value={editing.timeline} onChange={(v) => setEditing({ ...editing, timeline: v })} testid="admin-trans-timeline" />
          </div>

          <VideoUploadField
            label="Vertical video (9:16 MP4 recommended, max 50 MB)"
            value={editing.video_url}
            onChange={(v) => setEditing({ ...editing, video_url: v })}
            testid="admin-trans-video"
          />
          <ImageUploadField
            label="Cover / poster image (shown before tap, and as fallback)"
            value={editing.video_poster_url}
            onChange={(v) => setEditing({ ...editing, video_poster_url: v })}
            testid="admin-trans-poster-image"
          />

          <ImageUploadField
            label="Before photo (optional)"
            value={editing.before_image_url}
            onChange={(v) => setEditing({ ...editing, before_image_url: v })}
            testid="admin-trans-before-image"
            bucket="stories"
          />
          <ImageUploadField
            label="After photo (optional)"
            value={editing.after_image_url}
            onChange={(v) => setEditing({ ...editing, after_image_url: v })}
            testid="admin-trans-after-image"
            bucket="stories"
          />

          <LinkedProductsEditor
            products={products}
            value={editing.linked_products || []}
            onChange={(next) => setEditing({ ...editing, linked_products: next })}
          />

          <Field
            label='CTA text (optional, default: "Explore this pairing")'
            value={editing.cta_text}
            onChange={(v) => setEditing({ ...editing, cta_text: v })}
            testid="admin-trans-cta-text"
          />

          <div className="space-y-1">
            <Label className="text-xs">Language</Label>
            <div className="inline-flex rounded-full bg-emerald-50 border border-emerald-100 p-1 text-sm" data-testid="admin-trans-language">
              {[
                { key: "en", label: "English" },
                { key: "hi", label: "हिंदी" },
                { key: "all", label: "All" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setEditing({ ...editing, language: tab.key })}
                  className={`px-4 py-1 rounded-full font-medium transition ${
                    (editing.language || "en") === tab.key
                      ? "bg-emerald-700 text-white shadow-sm"
                      : "text-emerald-800 hover:bg-white/60"
                  }`}
                  data-testid={`admin-trans-lang-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-emerald-900/55">
              Used by the homepage video filter tabs.
            </p>
          </div>

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} id="t-active" data-testid="admin-trans-active" />
              <Label htmlFor="t-active">Active (shown publicly)</Label>
            </div>
            <Field label="Sort order" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: Number(v) || 0 })} testid="admin-trans-sortorder" type="number" inline />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-trans-save-btn">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const EMPTY_A = {
  slug: "",
  title: "",
  condition_key: "",
  meta_description: "",
  hero_excerpt: "",
  body_md: "",
  related_keys: [],
  tags: [],
  estimated_read_minutes: "",
  hero_image_url: "",
  secondary_image_url: "",
  faq: [],
  is_published: true,
  sort_order: 0,
};

// Wellness-support tag vocabulary (matches backend support_tags.py).
// Admins pick zero or more per article. Used for personalised dashboard reading.
const ARTICLE_TAG_VOCAB = [
  "thyroid_concern",
  "hormonal_support",
  "menopause_support",
  "blood_sugar_support",
  "cravings_support",
  "fatigue_support",
  "burnout_signal",
  "anxiety_signal",
  "mood_signal",
  "digestive_support",
  "metabolic_support",
  "liver_inflammation_support",
  "cardio_support",
  "skin_support",
  "autoimmune_signal",
  "medication_context",
];

export function ArticlesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_A);
  const [busy, setBusy] = useState(false);
  const [bulkFaqOpen, setBulkFaqOpen] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/learn");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing({ ...EMPTY_A }); setOpen(true); };
  const startEdit = (a) => {
    setEditing({
      ...a,
      related_keys: a.related_keys || [],
      tags: Array.isArray(a.tags) ? a.tags : [],
      estimated_read_minutes: a.estimated_read_minutes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        sort_order: Number(editing.sort_order) || 0,
        estimated_read_minutes: editing.estimated_read_minutes
          ? Number(editing.estimated_read_minutes) || null
          : null,
        tags: Array.isArray(editing.tags) ? editing.tags.slice(0, 12) : [],
        related_keys: typeof editing.related_keys === "string"
          ? editing.related_keys.split(",").map((s) => s.trim()).filter(Boolean)
          : (editing.related_keys || []),
        faq: Array.isArray(editing.faq) ? editing.faq.slice(0, 10) : [],
      };
      // Strip transient copilot-only fields (theme prompts) before saving
      delete payload._ai_hero_theme;
      delete payload._ai_secondary_theme;
      if (editing.id) {
        await api.put(`/admin/learn/${editing.id}`, payload);
      } else {
        await api.post(`/admin/learn`, payload);
      }
      toast.success("Saved");
      setOpen(false);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/admin/learn/${id}`);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-articles-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-display text-lg text-emerald-950">Wellness library</h3>
            <p className="text-xs text-emerald-900/60">SEO-friendly articles published at /learn/&lt;slug&gt;.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setBulkFaqOpen(true)}
              variant="outline"
              className="rounded-full border-emerald-200 hover:bg-emerald-50 text-emerald-900"
              data-testid="admin-article-bulk-faq-btn"
            >
              <Sparkles className="w-4 h-4 mr-1 text-emerald-700" /> Bulk FAQ
            </Button>
            <Button onClick={startNew} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-article-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New article
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {items.length === 0 ? (
            <p className="text-sm text-emerald-900/60">No articles yet.</p>
          ) : items.map((a) => (
            <div key={a.id} className="rounded-xl border border-emerald-100 p-4 flex items-start gap-3" data-testid={`admin-article-row-${a.id}`}>
              <div className="grow min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-emerald-950">{a.title}</span>
                  <a href={`/learn/${a.slug}`} target="_blank" rel="noreferrer noopener" className="text-xs text-emerald-700 inline-flex items-center gap-1 hover:underline">
                    /learn/{a.slug} <ExternalLink className="w-3 h-3" />
                  </a>
                  {!a.is_published ? <span className="text-[11px] uppercase tracking-[0.18em] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Draft</span> : null}
                </div>
                <div className="text-xs text-emerald-900/60 mt-1 line-clamp-2">{a.hero_excerpt}</div>
                {Array.isArray(a.tags) && a.tags.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {a.tags.slice(0, 5).map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 capitalize"
                      >
                        {t.replace(/_/g, " ")}
                      </span>
                    ))}
                    {a.tags.length > 5 ? (
                      <span className="text-[10px] text-emerald-900/50 self-center">
                        +{a.tags.length - 5}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => startEdit(a)} className="rounded-full text-emerald-700"><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(a.id)} className="rounded-full text-red-600"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-article-dialog">
          <DialogHeader>
            <DialogTitle>{editing.id ? "Edit article" : "New article"}</DialogTitle>
          </DialogHeader>

          {/* AI Copilot — pre-fills the form fields below */}
          <ArticleAiCopilot
            value={editing}
            onChange={(patch) => setEditing((prev) => ({ ...prev, ...patch }))}
            onApplyFaq={(faqList) => {
              if (!faqList?.length) return;
              setEditing((prev) => {
                const faqMarkdown = "\n\n## FAQ\n\n" + faqList.map(
                  (f) => `**${f.q}**\n\n${f.a}`
                ).join("\n\n");
                return {
                  ...prev,
                  body_md: (prev.body_md || "") + faqMarkdown,
                  faq: faqList,
                };
              });
            }}
            onApplyCta={(cta) => {
              if (!cta?.text) return;
              setEditing((prev) => {
                const ctaMarkdown = `\n\n[${cta.text}](${cta.url})`;
                return {
                  ...prev,
                  body_md: (prev.body_md || "") + ctaMarkdown,
                };
              });
            }}
          />

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Slug (URL)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })} testid="admin-article-slug" />
            <Field label="Condition key (e.g. pcos)" value={editing.condition_key} onChange={(v) => setEditing({ ...editing, condition_key: v })} testid="admin-article-condition-key" />
          </div>
          <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="admin-article-title" />
          <Field area label="Meta description (SEO, 60–160 chars)" rows={2} value={editing.meta_description} onChange={(v) => setEditing({ ...editing, meta_description: v })} testid="admin-article-meta" />
          <Field area label="Hero excerpt (first paragraph)" rows={3} value={editing.hero_excerpt} onChange={(v) => setEditing({ ...editing, hero_excerpt: v })} testid="admin-article-excerpt" />
          <Field area label="Body (Markdown — ## h2, ### h3, - bullet, **bold**, _italic_)" rows={14} value={editing.body_md} onChange={(v) => setEditing({ ...editing, body_md: v })} testid="admin-article-body" />
          <Field
            label="Related keys (comma-separated)"
            value={Array.isArray(editing.related_keys) ? editing.related_keys.join(", ") : editing.related_keys}
            onChange={(v) => setEditing({ ...editing, related_keys: v })}
            testid="admin-article-related"
          />

          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid="admin-article-tags-picker">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Wellness signals
            </div>
            <p className="text-[11px] text-emerald-900/65 mt-1 leading-relaxed">
              Tag this article with the support signals it speaks to. Members whose
              <code className="mx-1 px-1 bg-white border border-emerald-100 rounded">support_tags</code>
              overlap will see it on their dashboard.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {ARTICLE_TAG_VOCAB.map((t) => {
                const active = (editing.tags || []).includes(t);
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setEditing({
                      ...editing,
                      tags: active
                        ? (editing.tags || []).filter((x) => x !== t)
                        : [...(editing.tags || []), t],
                    })}
                    className={`rounded-full px-3 py-1.5 text-xs border transition select-none capitalize ${
                      active
                        ? "bg-emerald-700 text-white border-emerald-700 shadow-sm"
                        : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"
                    }`}
                    data-testid={`admin-article-tag-${t}`}
                  >
                    {active ? "✓ " : ""}{t.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <Field
            label="Estimated read minutes (optional)"
            type="number"
            value={editing.estimated_read_minutes}
            onChange={(v) => setEditing({ ...editing, estimated_read_minutes: v })}
            testid="admin-article-readmins"
          />

          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Switch checked={editing.is_published} onCheckedChange={(v) => setEditing({ ...editing, is_published: v })} id="a-pub" data-testid="admin-article-published" />
              <Label htmlFor="a-pub">Published (publicly visible)</Label>
            </div>
            <Field label="Sort order" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: Number(v) || 0 })} testid="admin-article-sortorder" type="number" inline />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-article-save-btn">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk FAQ generator — back-fills existing articles with AI FAQ */}
      <BulkFaqModal
        open={bulkFaqOpen}
        onClose={() => setBulkFaqOpen(false)}
        onApplied={reload}
      />
    </Card>
  );
}

function Field({ label, value, onChange, testid, type, area, rows, inline }) {
  if (inline) {
    return (
      <div className="flex items-center gap-2">
        <Label className="text-xs whitespace-nowrap">{label}</Label>
        <Input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-24 h-9" />
      </div>
    );
  }
  return (
    <div className="space-y-1 mt-2">
      <Label className="text-xs">{label}</Label>
      {area ? (
        <Textarea rows={rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="rounded-lg" />
      ) : (
        <Input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="rounded-lg" />
      )}    </div>
  );
}

function VideoUploadField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/video?bucket=stories", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      onChange(data.url);
      toast.success(`Uploaded · ${(data.size_bytes / (1024 * 1024)).toFixed(1)} MB`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1 mt-3">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste video URL or upload →"
          data-testid={testid}
          className="rounded-lg"
        />
        <input
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          ref={inputRef}
          onChange={onFile}
          className="hidden"
          data-testid={`${testid}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded-lg whitespace-nowrap"
          data-testid={`${testid}-upload-btn`}
        >
          <Upload className="w-4 h-4 mr-1" /> {busy ? `Uploading… ${progress}%` : "Upload video"}
        </Button>
      </div>
      {busy ? (
        <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden">
          <div className="h-full bg-emerald-700 transition-all" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
      {value ? (
        <video
          src={value}
          muted
          playsInline
          controls
          preload="metadata"
          className="mt-2 max-w-[180px] aspect-[9/16] object-cover rounded-lg border border-emerald-100 bg-emerald-950"
        />
      ) : null}
    </div>
  );
}



/* ------------------------------ Linked products ------------------------------ */

function LinkedProductsEditor({ products, value, onChange }) {
  const all = products || [];
  const used = new Set((value || []).map((v) => v.product_id).filter(Boolean));
  const remaining = all.filter((p) => !used.has(p.id));

  const addRow = () => {
    const next = [
      ...(value || []),
      {
        product_id: remaining[0]?.id || "",
        reason: "",
        sort_order: (value || []).length,
      },
    ];
    onChange(next);
  };
  const removeRow = (idx) => {
    const next = (value || []).filter((_, i) => i !== idx);
    onChange(next);
  };
  const update = (idx, patch) => {
    const next = (value || []).map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };
  const move = (idx, direction) => {
    const next = [...(value || [])];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((r, i) => ({ ...r, sort_order: i })));
  };

  return (
    <div className="space-y-2 mt-3 p-4 rounded-xl border border-emerald-100 bg-emerald-50/30" data-testid="admin-trans-linked-products">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
          <Sparkles className="w-3.5 h-3.5" /> Linked products
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addRow}
          disabled={remaining.length === 0}
          className="rounded-full"
          data-testid="admin-trans-linked-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add product
        </Button>
      </div>
      <p className="text-[11px] text-emerald-900/65 leading-relaxed">
        Optional — show the products this person used inside the reel modal. Reorder with the arrows; the first item appears first.
      </p>

      {(!value || value.length === 0) ? (
        <p className="text-xs text-emerald-900/55 italic mt-2">No products linked yet.</p>
      ) : (
        <ul className="space-y-2 mt-2">
          {value.map((row, idx) => {
            const product = all.find((p) => p.id === row.product_id);
            return (
              <li
                key={`${row.product_id || "blank"}-${idx}`}
                className="rounded-lg border border-emerald-100 bg-white p-3 flex flex-col sm:flex-row sm:items-start gap-3"
                data-testid={`admin-trans-linked-row-${idx}`}
              >
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="text-emerald-700 hover:text-emerald-900 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                    aria-label="Move up"
                    data-testid={`admin-trans-linked-up-${idx}`}
                  >
                    ▲
                  </button>
                  <GripVertical className="w-3.5 h-3.5 text-emerald-700/30 mx-auto" />
                  <button
                    type="button"
                    onClick={() => move(idx, +1)}
                    disabled={idx === (value.length - 1)}
                    className="text-emerald-700 hover:text-emerald-900 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                    aria-label="Move down"
                    data-testid={`admin-trans-linked-down-${idx}`}
                  >
                    ▼
                  </button>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <Label className="text-xs">Product</Label>
                    <select
                      value={row.product_id || ""}
                      onChange={(e) => update(idx, { product_id: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                      data-testid={`admin-trans-linked-product-${idx}`}
                    >
                      <option value="" disabled>Select a product…</option>
                      {all.map((p) => (
                        <option
                          key={p.id}
                          value={p.id}
                          disabled={used.has(p.id) && p.id !== row.product_id}
                        >
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Reason (short — why this person used it)</Label>
                    <Input
                      value={row.reason || ""}
                      onChange={(e) => update(idx, { reason: e.target.value })}
                      placeholder='e.g. "Helped balance her morning energy"'
                      className="mt-1 rounded-lg"
                      data-testid={`admin-trans-linked-reason-${idx}`}
                      maxLength={180}
                    />
                  </div>
                  {product?.image_url ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-900/60">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-8 h-8 rounded object-cover border border-emerald-100"
                      />
                      <span>{product.name}</span>
                    </div>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => removeRow(idx)}
                  className="rounded-full text-red-600 shrink-0 self-start"
                  aria-label="Remove product"
                  data-testid={`admin-trans-linked-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
